import React from "react";
import { createPortal } from "react-dom";
import { AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

import ModalAnimationWrapper from "./ModalAnimationWrapper";
import ModalOverlay from "./ModalOverlay";

export const letterModalPanelClass =
  "relative mx-4 w-full max-w-2xl rounded-xl bg-modalBg p-10 shadow-xl sm:p-12";

export const letterModalTitleClass =
  "mb-6 font-logo text-[1.625rem] leading-tight text-modalTitle sm:text-[1.875rem]";

export const letterModalBodyClass =
  "mb-8 font-josefin text-sm leading-relaxed text-modalBody";

export const letterModalCloseClass =
  "absolute right-5 top-5 rounded p-1 text-[#999999] transition hover:text-secondaryText";

function LetterDialog({
  open = true,
  onClose,
  title,
  children,
  footer,
  maxWidth = "max-w-lg",
  scrollable = false,
  centered = false,
  animationVariant = "scaleFade",
}) {
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <ModalOverlay key="letter-dialog-overlay">
          <ModalAnimationWrapper
            variant={animationVariant}
            className="flex h-full w-full items-center justify-center p-4"
          >
            <div
              className={`relative flex w-full flex-col ${maxWidth} rounded-xl bg-modalBg p-10 shadow-xl sm:p-12 ${
                scrollable ? "max-h-[90vh]" : ""
              }`}
              role="dialog"
              aria-modal="true"
            >
              <button
                type="button"
                onClick={onClose}
                className={letterModalCloseClass}
                aria-label="Fermer"
              >
                <X size={20} strokeWidth={1.5} />
              </button>

              {title && (
                <h2
                  className={`${letterModalTitleClass} shrink-0 ${
                    centered ? "text-center px-6 sm:px-8" : "pr-10"
                  }`}
                >
                  {title}
                </h2>
              )}

              {scrollable ? (
                <div className="modal-scroll min-h-0 flex-1">
                  <div className="modal-scroll-content">{children}</div>
                </div>
              ) : (
                children
              )}

              {footer && <div className="shrink-0 pt-2">{footer}</div>}
            </div>
          </ModalAnimationWrapper>
        </ModalOverlay>
      )}
    </AnimatePresence>,
    document.body
  );
}

export function LetterDialogActions({
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  primaryVariant = "green",
  secondaryVariant = "outline-red",
  centered = true,
  primaryDisabled = false,
}) {
  const primaryStyles = {
    green:
      "rounded-md bg-sageDark px-6 py-2.5 font-josefin text-sm font-medium text-white transition hover:bg-modalSage",
    purple:
      "rounded-md bg-modalPurple px-6 py-2.5 font-josefin text-sm font-medium text-white transition hover:bg-primaryText",
  };

  const secondaryStyles = {
    "outline-red":
      "rounded-md border border-modalRed bg-transparent px-6 py-2.5 font-josefin text-sm font-medium text-modalRed transition hover:bg-modalRed/5",
    "outline-purple":
      "rounded-md border border-modalPurple bg-transparent px-6 py-2.5 font-josefin text-sm font-medium text-modalPurple transition hover:bg-modalPurple/5",
    outline:
      "rounded-md border border-greyLight bg-white px-6 py-2.5 font-josefin text-sm font-medium text-secondaryText transition hover:bg-pageBg",
  };

  return (
    <div
      className={`flex flex-row flex-wrap gap-4 ${
        centered ? "justify-center" : "justify-end"
      }`}
    >
      {secondaryLabel && (
        <button
          type="button"
          onClick={onSecondary}
          className={secondaryStyles[secondaryVariant]}
        >
          {secondaryLabel}
        </button>
      )}
      {primaryLabel && (
        <button
          type="button"
          onClick={onPrimary}
          disabled={primaryDisabled}
          className={`${primaryStyles[primaryVariant]} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {primaryLabel}
        </button>
      )}
    </div>
  );
}

export default LetterDialog;
